import { DTMEventType, StringStringMap } from '@as-react/common-types';
import { isNullOrUndefined, mergeObjectDeep } from '@as-react/common-utils';
import { Target } from 'redux-beacon';
import { Options } from './types';

const keysToFilter = ['directCallRule'];

type DataLayer = {
  event?: Array<unknown>;
  product?: Array<unknown>;
  additionalProduct?: Array<unknown>;
  bundleProduct?: Array<unknown>;
};
type DirectCallLayer = { track: (rule: unknown, args?: unknown) => void };

declare const window: {
  digitalData: DataLayer;
  _satellite: DirectCallLayer;
};

const AdobeTagManager =
  (options: Options = {}): Target =>
  events => {
    const dataLayerName = (options.dataLayerName || 'digitalData') as keyof typeof window;
    const directCallLayerName = (options.directCallLayerName || '_satellite') as keyof typeof window;
    const enableDebug = options.enableDebug || false;

    if (typeof window === 'undefined') {
      return;
    }
    if (!window[dataLayerName]) {
      console.warn(
        `redux-beacon error: window.${dataLayerName} is not defined. Have you forgotten to include Adobe Tag Manager and dataLayer?`
      );
      return;
    }
    if (!window[directCallLayerName] || !(window[directCallLayerName] as DirectCallLayer).track) {
      console.warn(
        `redux-beacon error: window.${directCallLayerName} is not defined. Have you forgotten to include Adobe Tag Manager and dataLayer?`
      );
      return;
    }

    const logger = (...args: Array<unknown>) => enableDebug && console.debug('🚀 [Redux Beacon]', ...args);

    try {
      events.forEach(rawEvent => {
        const dataLayer = window[dataLayerName] as DataLayer;
        const directCallLayer = window[directCallLayerName] as DirectCallLayer;
        let directCallRules: Array<{ directCallRule: string; options?: unknown }> = [];

        if (rawEvent.directCallRule) {
          directCallRules = [...directCallRules, rawEvent];
        }
        logger('directCallRules', directCallRules);

        if (rawEvent.event) {
          directCallRules = [
            ...directCallRules,
            ...rawEvent.event
              .filter(
                (filterEvent: DTMEventType) =>
                  !isNullOrUndefined(filterEvent.eventInfo) &&
                  !isNullOrUndefined(filterEvent.eventInfo.directCallRule) &&
                  filterEvent.eventInfo.directCallRule === true
              )
              .map((filterEvent: DTMEventType) => filterEvent.eventInfo.ruleName),
          ].filter(Boolean);

          logger('directCallRules after merge', directCallRules);
        }

        const event: StringStringMap = Object.keys(rawEvent)
          .filter(key => keysToFilter.indexOf(key) === -1)
          .reduce(
            (obj, key) => ({
              ...obj,
              [key]: rawEvent[key],
            }),
            {}
          );

        if (event.type !== undefined) {
          switch (event.type) {
            case 'event':
              dataLayer.event!.push(event.payload);
              console.debug('Added event to dataLayer', JSON.stringify(dataLayer.event));
              break;
            default:
              break;
          }
        } else {
          logger('Original dataLayer', dataLayer);
          logger('To merge event', event);

          if (dataLayer.product?.length && event.product) {
            dataLayer.product = [];
          }

          if (dataLayer.additionalProduct?.length && event.additionalProduct) {
            dataLayer.additionalProduct = [];
          }

          if (dataLayer.bundleProduct?.length && event.bundleProduct) {
            dataLayer.bundleProduct = [];
          }

          const digitalDataObject = mergeObjectDeep(dataLayer, event);

          logger('Merged digitalDataObject', digitalDataObject);

          (window[dataLayerName] as DataLayer) = digitalDataObject;

          logger('After set', window[dataLayerName]);
        }

        if (directCallRules.length > 0 && directCallLayer) {
          directCallRules.forEach(directCallRule => {
            if (directCallRule.options) {
              directCallLayer.track(directCallRule.directCallRule, directCallRule.options);
            } else {
              directCallLayer.track(directCallRule.directCallRule || directCallRule);
            }
            logger(`track DirectcallRule: ${directCallRule.directCallRule || directCallRule}`);
          });
        }
      });
    } catch (e) {
      console.error('redux-beacon error in scripts', e);
    }
  };

export default AdobeTagManager;
